import React from "react";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import type { RfxViewMode } from "../../hooks/useRfxViewMode";
import type { RfxListPageMeta } from "../../hooks/useRfxList";
import type { RfxListItemResource } from "../../types/rfx-list.type";
import { getApprovalRowTarget } from "./approvalRowTarget";
import RFQGridView from "../legacy/RFQGridView";
import RFQListView from "../legacy/RFQListView";
import { toLegacyRfxRow } from "../legacy/toLegacyRfxRow";

export interface ApprovalsListProps {
	meta: RfxListPageMeta | undefined;
	rows: RfxListItemResource[];
	/**
	 * Current tab ("current" = pending) — determines whether rows target the
	 * `.../view` approve/reject affordance or the read-only `.../all` detail.
	 * See TASK-029 item 3.
	 */
	isPending?: boolean;
	/** Grid/list mode, owned by `RfxListPage`'s toolbar (legacy parity). */
	view: RfxViewMode;
}

/**
 * Approvals row rendering — plugs into 007's `RfxListPage` `renderList` slot.
 * Server-authoritative: rows render exactly what `useRfxList` returned for
 * the active tab (no client-side re-filtering). Click-through target is the
 * RFx detail page (`getApprovalRowTarget`), consistent for both the pending
 * pre-approval action (Current) and the actioned-row read view (Past).
 *
 * TASK-027 (second pass, team-lead ruling): shares the SAME restored legacy
 * `RFQGridView`/`RFQListView`/`RFQCard` T&Q uses, instead of the TASK-023
 * lookalike `ApprovalCard`/inline table (now deleted) — see `PanelList.tsx`'s
 * matching header comment for the full rationale (also applies here:
 * `toLegacyRfxRow` maps only the fields Approvals actually has, the legacy
 * components render the rest conditionally, and `linkOverride` preserves
 * `getApprovalRowTarget`'s detail-page target for both tabs).
 */
export const ApprovalsList = ({
	rows,
	isPending = false,
	view,
}: ApprovalsListProps) => {
	const { clientPaths } = useNavigationPaths();

	const legacyRows = rows.map((row) =>
		toLegacyRfxRow(
			row,
			clientPaths ? getApprovalRowTarget(clientPaths, row, isPending) : "#"
		)
	);

	return (
		<div data-testid="approvals-list">
			{view === "grid" ? (
				<div data-testid="approvals-grid">
					<RFQGridView
						rows={legacyRows}
						isLoading={false}
						onExtendClick={() => undefined}
						isSidebarCollapsed={false}
					/>
				</div>
			) : (
				<div data-testid="approvals-table">
					<RFQListView rows={legacyRows} isLoading={false} onExtendClick={() => undefined} />
				</div>
			)}
		</div>
	);
};
